
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle } from "lucide-react";
import Spinner from "@/components/ui/Spinner";
import { useCallbackProcessing } from "./hooks/useCallbackProcessing";
import { logger } from "@/services/LoggingService";

// Make props optional with sensible defaults
export interface CallbackProcessingProps {
  provider?: string;
  connectionId?: string | null;
  error?: string | null;
  onComplete?: () => void;
}

export function CallbackProcessing({ 
  provider = "Provider", 
  connectionId = null, 
  error = null, 
  onComplete = () => {} 
}: CallbackProcessingProps = {}) {
  const { processingCallback, callbackDetails } = useCallbackProcessing();
  
  // Log debug information to track component rendering
  React.useEffect(() => {
    logger.debug('CallbackProcessing', 'Component rendering', {
      processingCallback,
      callbackDetails,
      providedProvider: provider,
      providedConnectionId: connectionId,
      providedError: error
    });
  }, [processingCallback, callbackDetails, provider, connectionId, error]);
  
  // If not processing a callback, don't render anything
  if (!processingCallback && !callbackDetails) {
    return null;
  }
  
  // Use provided props or callback details if available
  const activeProvider = callbackDetails?.provider || provider;
  const activeConnectionId = callbackDetails?.connectionId || connectionId;
  const activeError = callbackDetails?.error || error;
  
  // Determine what to display based on connection state
  const isError = !!activeError;
  const isSuccess = !activeError && !!activeConnectionId;
  const message = isError 
    ? `Connection error: ${activeError}`
    : isSuccess 
      ? "Connection successful!" 
      : "Processing your connection...";
  
  return (
    <div className="w-full max-w-md mr-auto py-12">
      <Card>
        <CardHeader>
          <CardTitle className="text-center">{activeProvider} Connection</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center space-y-4 py-4">
            {!isSuccess && !isError && (
              <Spinner size="md" variant="primary" />
            )}
            {isSuccess && (
              <CheckCircle className="h-10 w-10 text-green-500" />
            )}
            {isError && (
              <AlertTriangle className="h-10 w-10 text-destructive" />
            )}
            
            <p className="text-center text-lg">{message}</p>
            
            <Button 
              onClick={onComplete}
              className="mt-4"
            >
              {isError ? "Try Again" : "Continue"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
